import { Eyebrow } from '../Eyebrow';

/**
 * Left rail of the /me/account settings hub per ADR-0015 + legacy
 * ADR-0030. Mirrors the AdminNavRail shape (entries[] with active-link
 * highlighting) but scoped to the eight self-service sections every
 * authenticated user owns. The header card surfaces the user's display
 * label + slug so the rail doubles as the "you are here" identity
 * chip.
 */
export interface AccountNavEntry {
    label: string;
    canonical: string;
    hint?: string;
}

export const ACCOUNT_NAV_ENTRIES: AccountNavEntry[] = [
    {
        label: 'Profile',
        canonical: '/me/account',
        hint: 'Handle, display name, bio, social links',
    },
    {
        label: 'Visibility',
        canonical: '/me/account/visibility',
        hint: 'Profile / favourites / collections defaults',
    },
    {
        label: 'Sign-in',
        canonical: '/me/account/sign-in',
        hint: 'Linked providers',
    },
    {
        label: 'Gwtags',
        canonical: '/me/account/gwtags',
        hint: 'Author claim proof wallet',
    },
    {
        label: 'Claims',
        canonical: '/me/account/claims',
        hint: 'Suggestions inbox + active claims',
    },
    {
        label: 'Collections',
        canonical: '/me/account/collections',
        hint: 'Curated package lists',
    },
    {
        label: 'Data',
        canonical: '/me/account/data',
        hint: 'GDPR export, account deletion',
    },
];

function isActive(canonical: string, currentPath: string): boolean {
    // The Profile section sits at the bare /me/account root.
    if (canonical === '/me/account') {
        return currentPath === '/me/account' || currentPath === '/me/account/profile';
    }
    return currentPath === canonical || currentPath.startsWith(canonical + '/');
}

export function AccountNavRail({
    currentPath,
    displayLabel,
    slug,
    onNavigate,
}: {
    currentPath: string;
    displayLabel: string | null | undefined;
    slug: string | null | undefined;
    onNavigate?: () => void;
}) {
    return (
        <nav
            aria-label="Account"
            class="flex flex-col gap-6 px-4 py-6 lg:px-5 lg:py-8"
        >
            <div class="border-b border-rule pb-4">
                <Eyebrow class="mb-1">Your account</Eyebrow>
                <div class="font-display text-[15px] italic leading-tight text-fg-primary">
                    {displayLabel ?? 'Signed in'}
                </div>
                {slug && (
                    <div class="mt-0.5 font-mono text-[11px] text-fg-muted">
                        @{slug}
                    </div>
                )}
                {slug && (
                    <a
                        href={`/u/${encodeURIComponent(slug)}`}
                        class="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.14em] text-accent hover:text-fg-primary"
                    >
                        View public profile →
                    </a>
                )}
            </div>
            <ul class="space-y-0.5">
                {ACCOUNT_NAV_ENTRIES.map((entry) => {
                    const active = isActive(entry.canonical, currentPath);
                    return (
                        <li key={entry.canonical}>
                            <a
                                href={entry.canonical}
                                onClick={onNavigate}
                                aria-current={active ? 'page' : undefined}
                                class={`group flex items-center gap-2 rounded px-2 py-1.5 text-[13.5px] transition-colors ${
                                    active
                                        ? 'bg-accent-glow text-fg-primary'
                                        : 'text-fg-secondary hover:bg-accent-glow/60 hover:text-fg-primary'
                                }`}
                            >
                                <span
                                    aria-hidden="true"
                                    class={`h-1.5 w-1.5 rounded-full ${
                                        active ? 'bg-accent' : 'bg-transparent'
                                    }`}
                                />
                                {entry.label}
                            </a>
                            {entry.hint && active && (
                                <p class="ml-4 mt-0.5 px-2 text-[11px] text-fg-muted">
                                    {entry.hint}
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
